import {snakeToCamel} from "../../util/stringUtil";
import {ParameterInputGrossFloorArea} from "./InputGrossFloorArea";
import {InputSectionIIEntriesDisabled} from "./InputSectionIIEntriesDisabled";
import InputSpecialPropertyFeaturesDisabled from "./InputSpecialPropertyFeaturesDisabled";
import ParameterInputBuildingCategoryNhk from "./ParameterInputBuildingCategoryNhk";
import ParameterInputCategoryButtons from "./ParameterInputCategoryButtons";
import ParameterInputCheckbox from "./ParameterInputCheckbox";
import ParameterInputCheckboxLeasehold from "./ParameterInputCheckboxLeasehold";
import ParameterInputConstructionCostsAdjust from "./ParameterInputConstructionCostsAdjust";
import ParameterInputDate from "./ParameterInputDate";
import ParameterInputFloodRiskCategory from "./ParameterInputFloodRiskCategory";
import ParameterInputNumber from "./ParameterInputNumber";
import ParameterInputRangeButtons from "./ParameterInputRangeButtons";
import ParameterInputSegment from "./ParameterInputSegment";
import ParameterInputText from "./ParameterInputText";
import ParameterInputToggle from "./ParameterInputToggle";
import ParameterInputValuationApproach from "./ParameterInputValuationApproach";
import ParameterInputYear from "./ParameterInputYear";
import {useInputParameterDef} from "./paramHooks";

/**
 * Display parameter input (all types)
 * @param {string} [parameter] parameter key (can be omitted, if parameter_details_def with parameter property is passed)
 * @param {RevaValueParameterType} [parameterType] type of parameter e.g. "AVM","META" or "DOC_PROPERTY"
 * @param {InputParameterDetailsDef|null} [parameter_details_def] optional parameter details definition, to use instead of parameter
 * @param {string|null} [label] optional (override) label to display (instead of label defined by input parameter details def)
 * @param {string|null} [associatedOutputApiParam] optional output parameter name, to display the value of the output parameter
 * @param {object} [events] events to call (currently only implemented for ParameterInputText#onBlur)
 * @param {React.Ref<HTMLDivElement> | undefined} ref allows to get a reference to underlying input component (currently only implemented for ParameterInputText)
 * @param {RevaSpecObjectInputRange[]|null} [ranges] optional value ranges from the spec row, rendered as select-buttons instead of the default input (#18858)
 * @param {string|null} [suggestionPlaceholder] Vorschlag, der nur als Platzhalter des leeren Felds erscheint und nie
 *        gespeichert wird (#19518; bisher nur in ParameterInputText umgesetzt)
 */
export default function ParameterInput({
    parameter,
    parameterType,
    parameter_details_def,
    label,
    associatedOutputApiParam,
    events,
    ref,
    page,
    ranges,
    suggestionPlaceholder,
}) {
    if (!parameter && parameter_details_def) {
        parameter = parameter_details_def.parameter;
    }

    const parameterData = useInputParameterDef(parameter, parameter_details_def);
    if (parameter === "segment") {
        return <ParameterInputSegment label={label} />;
    }

    // if Parameter is not a valid input for current segment, don't show input
    if (!parameterData) {
        console.info(`[ParameterInput] Parameter not found: ${parameter} (${parameterType})`);
        return null;
    }
    const id = snakeToCamel(`input_${parameter}`);
    const inputProps = {
        parameter,
        parameterType,
        label,
        id,
        parameter_details_def,
        associatedOutputParam: associatedOutputApiParam,
        events,
        ref,
        page,
        suggestionPlaceholder,
    };

    // location_flood_risk braucht den Klimadaten-Vorschlag als Default-Highlight, entscheidet daher
    // selbst zwischen Range-Auswahlbox und dem rückwärtskompatiblen Dropdown (ohne Spec-Ranges)
    if (parameterData.parameter === "location_flood_risk") {
        return <ParameterInputFloodRiskCategory {...inputProps} ranges={ranges} />;
    }

    //range werte haben keinen eigenen parameterData.type da reva spezifisch
    if (Array.isArray(ranges) && ranges.length > 0) {
        return <ParameterInputRangeButtons {...inputProps} ranges={ranges} />;
    }

    // Handle special hard-coded inputs
    switch (parameterData.parameter) {
        case "special_property_features_values":
            return <InputSpecialPropertyFeaturesDisabled {...inputProps} />;
        case "gross_floor_area":
            return <ParameterInputGrossFloorArea {...inputProps} originTab={"inspectionDerived"} />;
        case "section_ii_entry_value":
            return <InputSectionIIEntriesDisabled {...inputProps} />;
        case "construction_costs_adjust":
            return <ParameterInputConstructionCostsAdjust {...inputProps} />;
        case "building_category_nhk":
            return <ParameterInputBuildingCategoryNhk {...inputProps} />;
        case "prop_leasehold":
            return <ParameterInputCheckboxLeasehold {...inputProps} />;
        case "valuation_approach":
            return <ParameterInputValuationApproach {...inputProps} />;
        default: // do nothing
    }

    switch (parameterData.type) {
        case "boolean":
            return <ParameterInputCheckbox {...inputProps} />;
        case "toggle":
            return <ParameterInputToggle {...inputProps} />;
        case "category":
            return <ParameterInputCategoryButtons {...inputProps} />;
        case "category[]":
            return <ParameterInputCategoryButtons {...inputProps} />;
        case "double":
            return <ParameterInputNumber {...inputProps} />;
        case "integer":
            if (parameter.includes("year")) {
                return <ParameterInputYear {...inputProps} />;
            }
            return <ParameterInputNumber {...inputProps} />;
        case "string":
            return <ParameterInputText {...inputProps} />;
        case "date":
            return <ParameterInputDate {...inputProps} />;
        default:
            console.warn(`Unsupported Input Type: ${parameterData.type} (for parameter ${parameter})`);
            return null;
    }
}
